import {formatDate} from '@angular/common';
import {PyramidCategory} from 'src/app/components/categories-pyramid-display/categories-pyramid-display.component';
import {
  ActiveTeamDynamicVm,
  IndicatorDetailVm,
  IndicatorTabVm,
  OverallScoreVm,
  ScoreDevelopmentChipVm,
  ScoreDevelopmentVm,
  ScoreDistributionSegmentVm,
  StatementsVm,
  StatementVm,
  TeamOverviewCellVm,
  TeamOverviewColumnVm,
  TeamOverviewRowVm,
  TeamOverviewVm
} from '../models/team-dynamic.vm';

export type ColorMap = { [key: string]: string };

const ICON_BASE = '../../../assets/icons/5D/';
const OVERALL_ICON = '../../../assets/icons/5D/overall-icon.svg';

const CATEGORY_ICONS: ColorMap = {
  trust: 'trust-icon.svg',
  conflict: 'conflict-icon.svg',
  commitment: 'commitment-icon.svg',
  accountability: 'accountability-icon.svg',
  results: 'results-icon.svg'
};

export function getIntervalColorName(score: number | null | undefined, intervals: any[]): string {
  if (!score) {
    return 'grey';
  }
  const interval = (intervals ?? []).find((item: any) => item.min <= score && score <= item.max);
  return interval?.indicatorColor?.color ?? 'grey';
}

export function getScoreColor(score: number | null | undefined, intervals: any[], colors: ColorMap): string {
  return colors[getIntervalColorName(score, intervals)] ?? colors['grey'];
}

export function getScoreGradient(score: number | null | undefined, intervals: any[], colors: ColorMap): string {
  return colors[getIntervalColorName(score, intervals) + 'Gradient'] ?? colors['greyGradient'];
}

const FADE_COLORS: ColorMap = {
  green: '#DBF1EA',
  yellow: '#F3EEC7',
  orange: '#FFE7C8',
  red: '#FEE5E5',
  grey: '#DEE4F3'
};

export function getScoreFadeColor(score: number | null | undefined, intervals: any[]): string {
  return FADE_COLORS[getIntervalColorName(score, intervals)] ?? FADE_COLORS['grey'];
}

export function getCategoryIcon(slug: string | undefined): string {
  const key = slug ? Object.keys(CATEGORY_ICONS).find(name => slug.includes(name)) : undefined;
  return key ? ICON_BASE + CATEGORY_ICONS[key] : '';
}

export function mapPyramidCategories(teamDynamic: any, intervals: any[], colors: ColorMap): PyramidCategory[] {
  const details = teamDynamic?.current?.details;
  if (!details?.length) {
    return [];
  }
  const previousDetails = mapDetailsByUuid(teamDynamic?.previous?.details);

  return [...details].reverse().map((detail: any) => {
    const collection = detail.timedQuestionCollection;
    const score = detail.score ?? 0;
    const previous = previousDetails[collection?.uuid];
    const comparable = isEligible(detail) && isEligible(previous)
      && previous.score !== null && previous.score !== undefined;
    return {
      uuid: collection?.uuid,
      title: (collection?.title ?? '').trim(),
      score,
      icon: getCategoryIcon(collection?.slug),
      color: getScoreColor(score, intervals, colors),
      gradientColor: getScoreGradient(score, intervals, colors),
      trend: comparable ? getTrend(score, previous.score) : null,
      diff: comparable ? score - previous.score : null
    };
  });
}

export function mapOverallScore(teamDynamic: any, teamsCount: number, intervals: any[],
                                colors: ColorMap): OverallScoreVm {
  const score = teamDynamic?.current?.score ?? 0;
  const previousScore = teamDynamic?.previous?.score;
  const hasPrevious = previousScore !== undefined && previousScore !== null;

  return {
    score,
    color: getScoreColor(score, intervals, colors),
    gradientColor: getScoreGradient(score, intervals, colors),
    trend: hasPrevious ? getTrend(score, previousScore) : null,
    diff: hasPrevious ? score - previousScore : null,
    teamsCount
  };
}

export function mapScoreDevelopment(response: any): ScoreDevelopmentVm {
  const points = extractScoreDevelopmentList(response)
    .map((entry: any) => {
      const date = Object.keys(entry ?? {})[0];
      return date ? {date: date, score: entry[date]} : null;
    })
    .filter((point: any) => !!point && point.score !== null && point.score !== undefined)
    .sort((a: any, b: any) => a.date.localeCompare(b.date));

  return {
    labels: points.map((point: any) => formatDate(point.date, 'dd MMM yyyy', 'en-US')),
    values: points.map((point: any) => point.score)
  };
}

export function mapScoreDevelopmentChips(categories: PyramidCategory[], overallTitle: string,
                                         overallScore: OverallScoreVm): ScoreDevelopmentChipVm[] {
  const overall: ScoreDevelopmentChipVm = {
    uuid: null,
    title: overallTitle,
    icon: OVERALL_ICON,
    color: overallScore.color
  };

  return [
    overall,
    ...categories.map(category => ({
      uuid: category.uuid,
      title: category.title,
      icon: category.icon,
      color: category.color
    }))
  ];
}

export function mapTeamOverview(response: any, categories: PyramidCategory[], intervals: any[],
                                colors: ColorMap): TeamOverviewVm {
  const columns = mapTeamOverviewColumns(categories);
  const teams = response?.teamTeamDynamics ?? [];

  return {
    latestCheckIn: getLatestCheckIn(teams),
    columns,
    rows: teams.map((team: any) => mapTeamOverviewRow(team, columns, intervals, colors))
  };
}

function mapTeamOverviewColumns(categories: PyramidCategory[]): TeamOverviewColumnVm[] {
  return [...categories].reverse().map(category => ({
    uuid: category.uuid,
    title: category.title,
    icon: category.icon
  }));
}

function mapTeamOverviewRow(team: any, columns: TeamOverviewColumnVm[], intervals: any[],
                            colors: ColorMap): TeamOverviewRowVm {
  const currentDetails = mapDetailsByTitle(team?.details);
  const previousDetails = mapDetailsByTitle(team?.previous?.details);
  const overallScore = team?.score ?? null;
  const overallColor = getScoreColor(overallScore, intervals, colors);

  const cells: TeamOverviewCellVm[] = columns.map(column => {
    const current = currentDetails[column.title];
    const previous = previousDetails[column.title];
    const score = current?.score;
    const hasScore = score !== undefined && score !== null;
    const comparable = hasScore && isEligible(current) && isEligible(previous)
      && previous.score !== null && previous.score !== undefined;
    return {
      title: column.title,
      score: hasScore ? score : null,
      trend: comparable ? getTrend(score, previous.score) : null,
      fadeColor: getScoreFadeColor(hasScore ? score : null, intervals)
    };
  });

  return {
    teamUuid: team?.team?.uuid ?? '',
    teamName: team?.team?.name ?? '',
    cells,
    overallScore,
    overallColor
  };
}

function mapDetailsByTitle(details: any[] | undefined): { [key: string]: any } {
  return (details ?? []).reduce((acc: any, detail: any) => {
    const title = (detail?.timedQuestionCollection?.title ?? '').trim();
    if (title) {
      acc[title] = detail;
    }
    return acc;
  }, {});
}

function getLatestCheckIn(teams: any[]): string | null {
  const created = teams
    .map((team: any) => team?.created)
    .filter((date: any) => !!date)
    .sort((a: string, b: string) => b.localeCompare(a))[0];

  return created ? formatDate(created.replace(' ', 'T'), 'dd MMM yyyy', 'en-US') : null;
}

function mapDetailsByUuid(details: any[] | undefined): { [key: string]: any } {
  return (details ?? []).reduce((acc: any, detail: any) => {
    const uuid = detail?.timedQuestionCollection?.uuid;
    if (uuid) {
      acc[uuid] = detail;
    }
    return acc;
  }, {});
}

function isEligible(detail: any): boolean {
  return !!detail?.eligibleForResult;
}

function getTrend(score: number, previousScore: number | undefined): 'up' | 'down' | null {
  if (previousScore === undefined || previousScore === null || score === previousScore) {
    return null;
  }
  return score > previousScore ? 'up' : 'down';
}

function extractScoreDevelopmentList(response: any): any[] {
  if (!response || response.error) {
    return [];
  }
  if (Array.isArray(response.scores)) {
    return response.scores;
  }
  if (Array.isArray(response)) {
    return response;
  }
  const embedded = response._embedded;
  if (embedded) {
    const firstList = Object.keys(embedded).map(key => embedded[key]).find(value => Array.isArray(value));
    return firstList ?? [];
  }
  return [];
}

export function mapIndicatorTabs(categories: PyramidCategory[]): IndicatorTabVm[] {
  return categories.map(category => ({
    uuid: category.uuid,
    title: category.title,
    color: category.color
  }));
}

export function mapScoreDistribution(distribution: any[] | undefined, intervals: any[],
                                     colors: ColorMap): ScoreDistributionSegmentVm[] {
  const entries = parseDistribution(distribution);
  const total = countTeams(entries);

  return [...(intervals ?? [])]
    .sort((first: any, second: any) => first.min - second.min)
    .map((band: any) => {
      const colorName = band?.indicatorColor?.color ?? 'grey';
      const teams = countTeams(entries.filter(entry => band.min <= entry.score && entry.score <= band.max));
      return {
        colorName,
        color: colors[colorName] ?? colors['grey'],
        teams,
        percentage: total ? Math.round((teams / total) * 100) : 0
      };
    });
}

export function mapActiveTeamDynamic(response: any): ActiveTeamDynamicVm | null {
  const items = response && !response.error ? (response.workspaceTeamDynamics ?? []) : [];
  const active = items.find((item: any) => item?.status === 'active') ?? items[0];

  return active
    ? {uuid: active.uuid ?? '', target: active.target ?? null, teamVisibility: !!active.teamVisibility}
    : null;
}

export function mapIndicatorDetail(response: any, intervals: any[], colors: ColorMap,
                                   activeTeamDynamic: ActiveTeamDynamicVm | null): IndicatorDetailVm | null {
  const detail = response && !response.error ? response.detail : null;
  if (!detail) {
    return null;
  }
  const collection = detail.timedQuestionCollection;
  const score = detail.score ?? 0;
  const previousScore = detail.previousScore;
  const hasPrevious = previousScore !== undefined && previousScore !== null;

  const entries = parseDistribution(response.distribution);
  const teamsTotal = countTeams(entries);
  const target = activeTeamDynamic?.target ?? null;
  const teamsAffected = target === null ? 0 :
    countTeams(entries.filter(entry => entry.score < target));

  return {
    uuid: collection?.uuid ?? '',
    title: (collection?.title ?? '').trim(),
    description: collection?.description ?? '',
    icon: getCategoryIcon(collection?.slug),
    score,
    color: getScoreColor(score, intervals, colors),
    trend: hasPrevious ? getTrend(score, previousScore) : null,
    diff: hasPrevious ? score - previousScore : null,
    teamsAffected,
    teamsTotal,
    affectedPercentage: teamsTotal ? Math.round((teamsAffected / teamsTotal) * 100) : 0,
    distribution: mapScoreDistribution(response.distribution, intervals, colors)
  };
}

interface DistributionEntry {
  score: number;
  teams: number;
}

function parseDistribution(distribution: any[] | undefined): DistributionEntry[] {
  return (distribution ?? [])
    .map((entry: any) => {
      const score = Object.keys(entry ?? {})[0];
      return score === undefined ? null : {score: Number(score), teams: entry[score] ?? 0};
    })
    .filter((entry: any): entry is DistributionEntry => !!entry && !isNaN(entry.score))
    .sort((first: DistributionEntry, second: DistributionEntry) => first.score - second.score);
}

function countTeams(entries: DistributionEntry[]): number {
  return entries.reduce((total: number, entry: DistributionEntry) => total + entry.teams, 0);
}

const STATEMENT_ICON_BASE = '../../../assets/svg/statement-icon-';
export function mapStatements(response: any, intervals: any[], colors: ColorMap): StatementsVm {
  const reports = response && !response.error ? (response.questionReports ?? []) : [];

  const items: StatementVm[] = reports.map((report: any) => {
    const question = report?.question ?? {};
    const hasScore = !!report?.eligibleForResult && report?.score !== null && report?.score !== undefined;
    const colorName = hasScore ? getIntervalColorName(report.score, intervals) : 'gray';
    const previousScore = report?.previousScore;
    const hasPrevious = hasScore && question.type !== 'ranked'
      && previousScore !== null && previousScore !== undefined;
    const iconName = question.type === 'single' || question.type === 'open'
      ? 'gray'
      : (colorName === 'grey' ? 'gray' : colorName);

    return {
      uuid: report?.uuid ?? '',
      questionUuid: question.uuid ?? '',
      title: question.title ?? '',
      type: question.type ?? '',
      score: hasScore ? report.score : null,
      responses: report?.responses ?? 0,
      rating: hasScore ? Math.round(report.score / 10) : null,
      icon: STATEMENT_ICON_BASE + iconName + '.svg',
      color: getScoreColor(hasScore ? report.score : null, intervals, colors),
      trend: hasPrevious ? getTrend(report.score, previousScore) : null,
      diff: hasPrevious ? report.score - previousScore : null
    };
  });

  return {items};
}
